import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  FormGroup,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomSelect from "components/Common/custom-select";
import CustomDatePicker from "common/CustomDatePicker";
import TableContainerColumnTotal from "components/Common/TableContainerColumnTotal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLocation, useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";
import { fetchAbsenteeismReport, fetchCategories, fetchDepartments, fetchDesignations, fetchEmployeeDropdownAutoList, fetchLateAttendanceReport, resetAttendanceReport } from "store/actions";
import CustomMonthPicker from "common/CustomMonthPicker";
import Loader from "common/pageLoader";

const AttendanceReportAbsenteeism = () => {
  const [columns, setColumns] = useState([]);
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [reportData, setReportData] = useState([]);
  const toggle = () => setDropdownOpen((prev) => !prev);
  const {
    employeeReport,
    categories,
    departments,
    loading,
    error,
    success
  } = useSelector((state) => ({
    employeeReport: state.AttendanceReportReducer?.employeeReport ?? [],
    categories: state.CategoryMaster?.categories ?? [],
    departments: state.DepartmentMaster?.departments ?? [],

    loading: state.AttendanceReportReducer?.loading,
    error: state.AttendanceReportReducer?.error,
    success: state.AttendanceReportReducer?.success,
  }));

  useEffect(() => {
    dispatch(resetAttendanceReport())
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchCategories(unitId));
    dispatch(fetchDepartments(unitId));
  }, [dispatch]);
  const formik = useFormik({
    initialValues: { category: "", department: "", month: null },
    validationSchema: Yup.object({ month: Yup.string().nullable().required("Month is required") }),
    onSubmit: (values) => {
      // console.log("Form Values:", values);
      const formData = new FormData();
      formData.append("dep_id", values.department || 0);
      formData.append("cat_id", values.category || 0);
      formData.append("month", values.month);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      dispatch(fetchAbsenteeismReport(formData));
    },
  });
  const handleReset = () => {
    formik.resetForm();
    dispatch(resetAttendanceReport());
  };

  useEffect(() => {
    if (!employeeReport || employeeReport.length === 0) {
      setReportData([]);
      return;
    }

    const updated = employeeReport.map(emp => {
      let totalWorking = 0;
      let totalPresent = 0;
      let totalAbsent = 0;

      Object.keys(emp).forEach(key => {
        if (key.startsWith("week_")) totalWorking += Number(emp[key]) || 0;
        if (key.startsWith("present_")) totalPresent += Number(emp[key]) || 0;
        if (key.startsWith("absent_")) totalAbsent += Number(emp[key]) || 0;
      });

      const totalPercent = totalWorking === 0 ? 0 : (totalAbsent / totalWorking) * 100;

      return {
        ...emp,
        month_working: totalWorking,
        month_present: totalPresent,
        month_absent: totalAbsent,
        month_absent_percent: totalPercent
      };
    });

    setReportData(updated);
  }, [employeeReport]);


  const getWeeksInMonth = (month, year) => {
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);

    const startWeek = Math.ceil((start.getDate() - start.getDay()) / 7) + 1;
    const endWeek = Math.ceil((end.getDate() - end.getDay()) / 7) + 1;

    return endWeek;
  };

  const generateColumns = (month, year) => {
    const selectedDate = new Date(year, month - 1, 1);
    const monthName = selectedDate.toLocaleString("default", { month: "short" });

    const noOfWeeks = getWeeksInMonth(month, year);

    let weekCols = [];

    for (let i = 1; i <= noOfWeeks; i++) {
      weekCols.push({
        Header: `${monthName}-${year} (Week ${i})`,
        className: "text-center",
        columns: [
          { Header: "Working Days", accessor: `week_${i}`, sum: true },
          { Header: "Present", accessor: `present_${i}`, sum: true },
          { Header: "Absent", accessor: `absent_${i}`, sum: true },
          // { Header: "Absent %", accessor: `absenteeism_${i}`, average: true }
          { Header: "Absent %", accessor: `absenteeism_${i}`, isPercentage: true, workingAccessor: `week_${i}`, absentAccessor: `absent_${i}` }
        ]
      });
    }

    const totalCols = {
      Header: `${monthName}-${year} Total`,
      className: "text-center",
      headerClassName: "bg-light text-center fw-bold",
      columns: [
        { Header: "Working Days", accessor: "month_working", sum: true },
        { Header: "Present", accessor: "month_present", sum: true },
        { Header: "Absent", accessor: "month_absent", sum: true },
        {
          Header: "Absent %", accessor: "month_absent_percent", isPercentage: true, workingAccessor: "month_working", absentAccessor: "month_absent",
          Cell: ({ value }) => `${Number(value || 0).toFixed(2)}%`
        }
      ]
    };

    return [
      // Remove this S.No from actual accessor table (UI only)
      { Header: "S.No", Cell: ({ row }) => employeeReport.length - row.index, className: "serial-no text-center", applyHeaderClass: true },
      { Header: "Code", accessor: "emp_code" },
      { Header: "Name", accessor: "emp_name" },
      { Header: "Dept", accessor: "department" },
      ...weekCols,
      totalCols
    ];
  };

  useEffect(() => {
    if (employeeReport?.length > 0 && formik.values.month) {
      const selectedDate = new Date(formik.values.month);
      const month = selectedDate.getMonth() + 1;
      const year = selectedDate.getFullYear();

      const generatedCols = generateColumns(month, year);
      setColumns(generatedCols);
    }
  }, [employeeReport, formik.values.month]);


  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Attendance" breadcrumbItem="Absenteeism" />
        <Card>
          <CardBody>
            <form onSubmit={formik.handleSubmit}>
              <Row className="gy-3">
                <Col md={3}>
                  <Label>Category</Label>
                  <CustomSelect
                    id="category"
                    name="category"
                    options={categories.map(cat => ({ value: cat.empCatgryid, label: cat.empCategory }))}
                    value={
                      categories
                        .map(cat => ({ value: cat.empCatgryid, label: cat.empCategory }))
                        .find(cat => cat.value === formik.values.category) || null
                    }
                    onChange={val => formik.setFieldValue('category', val ? val.value : '')}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.category && formik.errors.category ? 'is-invalid' : ''}`}
                  />
                </Col>

                <Col md={3}>
                  <Label>Department</Label>
                  <CustomSelect
                    id="department"
                    name="department"
                    options={departments.map(dept => ({ value: dept.departmentId, label: dept.departmentname }))}
                    value={
                      departments
                        .map(dept => ({ value: dept.departmentId, label: dept.departmentname }))
                        .find(dept => dept.value === formik.values.department) || null
                    }
                    onChange={val => formik.setFieldValue('department', val ? val.value : '')}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.department && formik.errors.department ? 'is-invalid' : ''}`}
                  />
                </Col>
                <Col md={3} >
                  <FormGroup>
                    <Label for="month">
                      Month <span className="text-danger">*</span>
                    </Label>
                    <CustomMonthPicker
                      name="month"
                      value={formik.values.month}
                      onChange={(date) => {
                        formik.setFieldValue("month", date);
                        formik.setFieldTouched("month", true, true); // force validation
                      }}
                      onBlur={() => formik.setFieldTouched("month", true)}
                      className={`form-control ${formik.touched.month && formik.errors.month ? "is-invalid" : ""}`}
                    />

                    {formik.touched.month && formik.errors.month && (
                      <div className="text-danger">{formik.errors.month}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col className="d-flex mob-center align-items-center justify-content-end">
                  <Button type="submit" className="btn button-search me-2 mt-2">
                    <i className="fas fa-search me-1" /> Search
                  </Button>
                  <Button type="button" className="btn button-cancel me-2 mt-2" onClick={handleReset}>
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                  {employeeReport && employeeReport.length > 0 && (
                    <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                      <DropdownToggle color="primary" className="btn button-export mt-2">
                        <i className="fas fa-file-export me-1" /> Export
                      </DropdownToggle>

                      <DropdownMenu>
                        <DropdownItem onClick={() => exportEmployeeToExcel(employeeReport, authDetails?.unit_fullname, authDetails?.unit_address, "Absenteeism Report")}>
                          <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                        </DropdownItem>

                        <DropdownItem onClick={() => exportEmployeeToPDF(employeeReport, authDetails?.unit_fullname, authDetails?.unit_address, "Absenteeism Report", columns)}>
                          <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <TableContainerColumnTotal
              columns={columns}
              data={reportData}
              isGlobalFilter={true}
              customPageSize={10}
              showTotalRow={true}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default AttendanceReportAbsenteeism;
